import { useCallback, useState } from "react";

import { blobToDataUrl, compressChatImage } from "~/libs/image.lib";
import { MAX_IMAGES_PER_MESSAGE } from "./constants";
import type { PendingImage } from "./types";

/** Manages pending image attachments for the chat input. */
export const useChatImages = () => {
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);

  const addImages = useCallback(
    async (files: File[]) => {
      const imageFiles = files.filter((f) => f.type.startsWith("image/"));
      if (imageFiles.length === 0) return;

      const remaining = MAX_IMAGES_PER_MESSAGE - pendingImages.length;
      const toAdd = imageFiles.slice(0, remaining);

      const placeholders: PendingImage[] = toAdd.map((f) => ({
        id: crypto.randomUUID(),
        dataUrl: null,
        mediaType: f.type,
        status: "processing",
      }));

      setPendingImages((prev) => [...prev, ...placeholders]);

      for (let idx = 0; idx < toAdd.length; idx++) {
        const file = toAdd[idx];
        const placeholder = placeholders[idx];
        compressChatImage(file)
          .then((blob) =>
            blobToDataUrl(blob).then((dataUrl) => ({
              dataUrl,
              mediaType: blob.type,
            })),
          )
          .then(({ dataUrl, mediaType }) => {
            setPendingImages((prev) =>
              prev.map((img) =>
                img.id === placeholder.id
                  ? { ...img, dataUrl, mediaType, status: "ready" }
                  : img,
              ),
            );
          })
          .catch(() => {
            setPendingImages((prev) =>
              prev.filter((img) => img.id !== placeholder.id),
            );
          });
      }
    },
    [pendingImages.length],
  );

  const removeImage = useCallback((id: string) => {
    setPendingImages((prev) => prev.filter((img) => img.id !== id));
  }, []);

  const clearImages = useCallback(() => setPendingImages([]), []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const imageFiles = Array.from(e.clipboardData.files).filter((f) =>
        f.type.startsWith("image/"),
      );
      if (imageFiles.length > 0) {
        e.preventDefault();
        addImages(imageFiles);
      }
    },
    [addImages],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        addImages(Array.from(e.target.files));
      }
      e.target.value = "";
    },
    [addImages],
  );

  const isProcessingImages = pendingImages.some(
    (img) => img.status === "processing",
  );

  return {
    pendingImages,
    addImages,
    removeImage,
    clearImages,
    handlePaste,
    handleFileInputChange,
    isProcessingImages,
  };
};
